import { useClosetStore } from "../state/store";
import type { CeilingType } from "../domain/types";

/** Left-rail control panel for editing the ClosetSpec. */
export function ControlsPanel(): JSX.Element {
  const {
    spec,
    patch,
    patchCeiling,
    patchMaterial,
    updateSection,
    addSection,
    removeSection,
  } = useClosetStore();

  return (
    <aside className="controls">
      <h2>Closet</h2>
      <Field label="Total width (cm)">
        <input
          type="number"
          value={spec.totalWidth}
          step={1}
          onChange={(e) =>
            patch({ totalWidth: parseFloat(e.target.value) || 0 })
          }
        />
      </Field>
      <Field label="Depth (cm)">
        <input
          type="number"
          value={spec.depth}
          step={1}
          onChange={(e) => patch({ depth: parseFloat(e.target.value) || 0 })}
        />
      </Field>
      <Field label="Toe kick height (cm)">
        <input
          type="number"
          value={spec.toeKickHeight}
          step={0.5}
          onChange={(e) =>
            patch({ toeKickHeight: parseFloat(e.target.value) || 0 })
          }
        />
      </Field>

      <h2>Ceiling</h2>
      <Field label="Type">
        <select
          value={spec.ceiling.type}
          onChange={(e) =>
            patchCeiling({ type: e.target.value as CeilingType })
          }
        >
          <option value="flat">Flat</option>
          <option value="slanted">Slanted (shed)</option>
          <option value="gable">Gable (peak in middle)</option>
        </select>
      </Field>
      <Field
        label={
          spec.ceiling.type === "gable"
            ? "Eave height (cm)"
            : "Left height (cm)"
        }
      >
        <input
          type="number"
          value={spec.ceiling.heightLeft}
          step={1}
          onChange={(e) =>
            patchCeiling({ heightLeft: parseFloat(e.target.value) || 0 })
          }
        />
      </Field>
      {spec.ceiling.type !== "flat" && (
        <Field
          label={
            spec.ceiling.type === "gable"
              ? "Peak height (cm)"
              : "Right height (cm)"
          }
        >
          <input
            type="number"
            value={spec.ceiling.heightRight}
            step={1}
            onChange={(e) =>
              patchCeiling({ heightRight: parseFloat(e.target.value) || 0 })
            }
          />
        </Field>
      )}

      <h2>Material</h2>
      <Field label="Carcass thickness (cm)">
        <input
          type="number"
          value={spec.material.carcassThickness}
          step={0.1}
          onChange={(e) =>
            patchMaterial({ carcassThickness: parseFloat(e.target.value) || 0 })
          }
        />
      </Field>
      <Field label="Back thickness (cm)">
        <input
          type="number"
          value={spec.material.backThickness}
          step={0.1}
          onChange={(e) =>
            patchMaterial({ backThickness: parseFloat(e.target.value) || 0 })
          }
        />
      </Field>
      <Field label="Sheet (W × L, cm)">
        <span style={{ display: "flex", gap: 4 }}>
          <input
            type="number"
            value={spec.material.sheetWidth}
            onChange={(e) =>
              patchMaterial({ sheetWidth: parseFloat(e.target.value) || 0 })
            }
          />
          <input
            type="number"
            value={spec.material.sheetLength}
            onChange={(e) =>
              patchMaterial({ sheetLength: parseFloat(e.target.value) || 0 })
            }
          />
        </span>
      </Field>
      <Field label="Kerf (cm)">
        <input
          type="number"
          value={spec.material.kerf}
          step={0.05}
          onChange={(e) =>
            patchMaterial({ kerf: parseFloat(e.target.value) || 0 })
          }
        />
      </Field>

      <h2>
        Sections
        <button onClick={addSection} style={{ float: "right" }}>
          + Add
        </button>
      </h2>
      {spec.sections.map((sec, i) => (
        <div key={sec.id} className="section">
          <header>
            <strong>Section {i + 1}</strong>
            <button onClick={() => removeSection(sec.id)}>×</button>
          </header>
          <Field label="Width (cm)">
            <input
              type="number"
              value={sec.width}
              step={1}
              onChange={(e) =>
                updateSection(sec.id, {
                  width: parseFloat(e.target.value) || 0,
                })
              }
            />
          </Field>
          <Field label="Adjustable shelves">
            <input
              type="number"
              value={sec.shelfCount}
              min={0}
              onChange={(e) =>
                updateSection(sec.id, {
                  shelfCount: parseInt(e.target.value) || 0,
                })
              }
            />
          </Field>
          <label className="check">
            <input
              type="checkbox"
              checked={sec.hangingRod}
              onChange={(e) =>
                updateSection(sec.id, { hangingRod: e.target.checked })
              }
            />{" "}
            Hanging rod
          </label>
          <label className="check">
            <input
              type="checkbox"
              checked={sec.drawerStack}
              onChange={(e) =>
                updateSection(sec.id, { drawerStack: e.target.checked })
              }
            />{" "}
            Drawer stack (4 drawers)
          </label>
        </div>
      ))}
    </aside>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="field">
      <span>{label}</span>
      {children}
    </label>
  );
}
